import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  getServiceHealthStatus,
  getServiceHealthHistory,
  postServiceHealthRunAll,
  postServiceHealthRunOne,
  putServiceHealthIgnore,
} from "../../helpers/fakebackend_helper";

// Polled every 30s while the panel is mounted. The actual probes run every
// SERVICE_HEALTH_INTERVAL_MIN (default 2 min) on the backend — this poll just
// re-reads the latest snapshot.
const POLL_MS = 30_000;

// Persist the last service snapshot so the table renders the known services +
// their last-known status INSTANTLY on load/refresh, instead of an empty table
// that only fills after a fetch/probe. The background poll then updates
// latency / last-check / status in place.
const CACHE_KEY = "bl_services_snapshot";
const loadServicesCache = () => {
  try { const v = JSON.parse(localStorage.getItem(CACHE_KEY)); return Array.isArray(v) ? v : []; }
  catch { return []; }
};
const saveServicesCache = (services) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(services || [])); } catch { /* quota/disabled */ }
};

const GROUP_META = {
  admin_direct:       { label: "Admin Direct",        icon: "ri-cloud-line",       description: "Third-party services the admin backend depends on directly." },
  biorlabs_federated: { label: "Biorlabs Subsystems", icon: "ri-stack-line",       description: "Subsystems on biorlabs-backend, each fronting one or more external APIs." },
  biorlabs_self:      { label: "Biorlabs Backend",    icon: "ri-server-line",      description: "Top-level liveness of the consumer wallet's backend." },
};
const GROUP_ORDER = ["admin_direct", "biorlabs_federated", "biorlabs_self"];

const STATE_TINT = {
  ok:      { bg: "rgba(0,205,82,0.14)",   fg: "#7be09a", dot: "#2BD56F", label: "Healthy" },
  fail:    { bg: "rgba(255,93,108,0.14)", fg: "#ff8a98", dot: "#ff5d6c", label: "Failing" },
  skipped: { bg: "rgba(255,255,255,0.06)",fg: "#b3b6c4", dot: "#777a8a", label: "Not configured" },
  unknown: { bg: "rgba(255,255,255,0.04)",fg: "#9a9caa", dot: "#666",    label: "Pending" },
  running: { bg: "rgba(243,175,76,0.16)", fg: "#ffd199", dot: "#f3af4c", label: "Checking…" },
  muted:   { bg: "rgba(255,255,255,0.04)",fg: "#9a9caa", dot: "#666",    label: "Muted" },
};

function formatRelative(input) {
  if (!input) return "—";
  const ts = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (!Number.isFinite(ts)) return "—";
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 5)  return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleString();
}

function formatDuration(start, end = Date.now()) {
  if (!start) return null;
  const ts = start instanceof Date ? start.getTime() : new Date(start).getTime();
  if (!Number.isFinite(ts)) return null;
  const ms = Math.max(0, end - ts);
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

const errMessage = (err) =>
  typeof err === "string"
    ? err
    : err?.response?.data?.message || err?.message || "Unknown error";

const StatusPill = ({ state }) => {
  const tint = STATE_TINT[state] || STATE_TINT.unknown;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 500,
      background: tint.bg,
      color: tint.fg,
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: tint.dot,
        boxShadow: state === "running" ? `0 0 0 4px ${tint.dot}33` : "none",
      }} />
      {tint.label}
    </span>
  );
};

/**
 * 24h sparkline. Each history point is one pixel-column; green = ok, red = fail.
 * Gaps between probe ticks remain blank (no fill) so eyes don't pattern-match
 * over outage windows that happen to span low-data periods.
 */
function HistorySparkline({ points, hours }) {
  if (!points || points.length === 0) {
    return <div style={{ fontSize: 12, color: "var(--bl-text-soft)", padding: "8px 0" }}>No history yet — probes start populating within a few minutes.</div>;
  }
  const w = 720, h = 36;
  const now = Date.now();
  const windowMs = hours * 3600_000;
  const xFor = (iso) => {
    const t = new Date(iso).getTime();
    const frac = 1 - (now - t) / windowMs;
    return Math.max(0, Math.min(w, frac * w));
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: h, display: "block" }} preserveAspectRatio="none">
      {points.map((p, i) => {
        const x = xFor(p.checked_at);
        const color = p.status === "ok" ? "#2BD56F" : p.status === "fail" ? "#ff5d6c" : "#777a8a";
        return <rect key={i} x={x.toFixed(1)} y={6} width={2} height={h - 12} fill={color} opacity={0.85} />;
      })}
      <line x1={0} x2={w} y1={h - 1} y2={h - 1} stroke="var(--bl-line)" strokeWidth={0.5} />
    </svg>
  );
}

function ServiceRow({ service, onForceRun, onToggleIgnore, canManage, expanded, onToggle, history, historyLoading }) {
  const isIgnored = !!service.is_ignored;
  const rawState = service.status || "unknown";
  const state = isIgnored ? "muted" : rawState;
  const isFailing = !isIgnored && rawState === "fail";
  const downSince = isFailing ? service.last_failed_at : null;
  const rowOpacity = isIgnored ? 0.5 : 1;
  return (
    <>
      <tr style={{ borderBottom: expanded ? "none" : "1px solid var(--bl-line)", opacity: rowOpacity, filter: isIgnored ? "grayscale(0.6)" : "none" }}>
        <td style={{ padding: "0.85rem 1rem", minWidth: 240 }}>
          <button
            type="button"
            onClick={onToggle}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "inherit",
              width: "100%",
            }}
          >
            <i
              className={`ri-arrow-${expanded ? "down" : "right"}-s-line`}
              style={{ color: "var(--bl-text-soft)", fontSize: 16, flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.2 }}>{service.display_name}</div>
              <div style={{
                marginTop: 2,
                fontSize: 11,
                color: "var(--bl-text-soft)",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              }}>{service.service_key}</div>
            </div>
          </button>
        </td>
        <td style={{ padding: "0.85rem 1rem" }}>
          <StatusPill state={state} />
        </td>
        <td style={{ padding: "0.85rem 1rem", color: "var(--bl-text-soft)", fontSize: 12, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {service.latency_ms != null ? `${service.latency_ms} ms` : "—"}
        </td>
        <td style={{ padding: "0.85rem 1rem", color: "var(--bl-text-soft)", fontSize: 12, whiteSpace: "nowrap" }}>
          {service.last_checked_at ? formatRelative(service.last_checked_at) : "—"}
        </td>
        <td style={{ padding: "0.85rem 1rem", color: isFailing ? "#ff8a98" : "var(--bl-text-soft)", fontSize: 12, whiteSpace: "nowrap" }}>
          {downSince ? `down ${formatDuration(downSince)}` : "—"}
        </td>
        <td style={{ padding: "0.85rem 1rem", color: isFailing ? "#ff8a98" : "var(--bl-text-soft)", fontSize: 12, maxWidth: 360 }}>
          <div title={service.message || ""} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {service.message || (state === "ok" ? "—" : state === "skipped" ? "env vars not set" : "")}
          </div>
        </td>
        <td style={{ padding: "0.85rem 1rem", textAlign: "right", whiteSpace: "nowrap" }}>
          {canManage && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleIgnore(service.service_key, !isIgnored); }}
                title={isIgnored ? "Re-include this probe in the failing count" : "Hide this probe from the failing count"}
                style={{
                  padding: "5px 9px",
                  borderRadius: 6,
                  border: "1px solid var(--bl-line-strong, #2b2c3f)",
                  background: isIgnored ? "rgba(87,88,252,0.16)" : "rgba(255,255,255,0.03)",
                  color: "var(--bl-text-strong, #e5e7f2)",
                  fontSize: 12,
                  cursor: "pointer",
                  marginRight: 6,
                }}
              >
                <i className={`ri-${isIgnored ? "volume-up-line" : "volume-mute-line"}`} style={{ marginRight: 4, fontSize: 12 }} />
                {isIgnored ? "Unmute" : "Ignore"}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onForceRun(service.service_key); }}
                style={{
                  padding: "5px 11px",
                  borderRadius: 6,
                  border: "1px solid var(--bl-line-strong, #2b2c3f)",
                  background: "rgba(255,255,255,0.03)",
                  color: "var(--bl-text-strong, #e5e7f2)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                <i className="ri-refresh-line" style={{ marginRight: 4, fontSize: 12 }} />
                Probe now
              </button>
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "rgba(255,255,255,0.02)", borderBottom: "1px solid var(--bl-line)" }}>
          <td colSpan={7} style={{ padding: "0.85rem 1rem" }}>
            <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              Past 24 hours
            </div>
            {historyLoading ? (
              <div style={{ fontSize: 12, color: "var(--bl-text-soft)", padding: "8px 0" }}>Loading history…</div>
            ) : (
              <HistorySparkline points={history?.points || []} hours={history?.hours || 24} />
            )}
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--bl-text-soft)", display: "flex", justifyContent: "space-between" }}>
              <span>24h ago</span>
              <span>{history && history.points ? `${history.points.length} probe ticks` : ""}</span>
              <span>now</span>
            </div>
            {service.last_ok_at && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--bl-text-soft)" }}>
                Last healthy: {formatRelative(service.last_ok_at)}
                {service.last_failed_at ? ` · Last failed: ${formatRelative(service.last_failed_at)}` : ""}
                {service.consecutive_failures > 0 ? ` · ${service.consecutive_failures} consecutive failures` : ""}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function SummaryCard({ label, total, ok, failed, skipped, icon, accent = "var(--bl-accent)" }) {
  const accentRgb = accent.includes("success") ? "0,205,82" : "87,88,252";
  const anyFailed = failed > 0;
  const allOk = total > 0 && ok === total - skipped;
  return (
    <div className="bl-glass" style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: `rgba(${accentRgb}, 0.16)`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <i className={icon} style={{ color: accent, fontSize: 20 }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{
          marginTop: 2,
          fontSize: "clamp(1.4rem, 3.5vw, 1.75rem)",
          fontFamily: "var(--bl-font-numeric)",
          color: "#fff",
          fontWeight: 700,
          lineHeight: 1.1,
          display: "flex",
          alignItems: "baseline",
          gap: 6,
        }}>
          <span style={{ color: allOk ? "var(--bl-success, #2BD56F)" : anyFailed ? "#ff5d6c" : "#fff" }}>{ok}</span>
          <span style={{ color: "var(--bl-text-soft)", fontSize: "0.7em", fontWeight: 500 }}>/ {total}</span>
        </div>
        <div style={{ marginTop: 2, fontSize: 12, color: "var(--bl-text-soft)" }}>
          {anyFailed ? <span style={{ color: "#ff8a98" }}>{failed} failing</span> : total === 0 ? "—" : skipped > 0 ? `${skipped} unconfigured` : "All healthy"}
        </div>
      </div>
    </div>
  );
}

const TableHeader = () => (
  <thead>
    <tr style={{ background: "rgba(var(--bl-accent-rgb), 0.06)", borderBottom: "1px solid var(--bl-line-strong)" }}>
      {["Service", "Status", "Latency", "Last checked", "Duration", "Message", ""].map((h, i) => (
        <th key={i} style={{
          padding: "0.65rem 1rem",
          textAlign: i === 6 ? "right" : "left",
          fontSize: 11,
          color: "var(--bl-text-soft)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}>{h}</th>
      ))}
    </tr>
  </thead>
);

const ServicesPanel = () => {
  const is_super_admin = useSelector((s) => s.Profile.is_super_admin);
  const permissions    = useSelector((s) => s.Profile.permissions);
  const canManage      = is_super_admin || (permissions || []).includes("service_health.manage");

  // Hydrate from the cached snapshot so the table is populated on first paint.
  const [services, setServices] = useState(loadServicesCache);
  const [loading, setLoading] = useState(() => loadServicesCache().length === 0);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const [expanded, setExpanded] = useState({}); // serviceKey -> bool
  const [history, setHistory] = useState({});   // serviceKey -> { points, hours }
  const [historyLoading, setHistoryLoading] = useState({}); // serviceKey -> bool

  // Track running ticks per service so the row can show "Checking…" briefly.
  const [running, setRunning] = useState({});

  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const loadStatus = useCallback(async () => {
    try {
      const body = await getServiceHealthStatus();
      if (!isMountedRef.current) return;
      const next = body?.services || [];
      setServices(next);
      saveServicesCache(next);
      setLastFetchedAt(Date.now());
      setError(null);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(errMessage(err));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  // Initial load + 30s poll while document is visible.
  useEffect(() => {
    loadStatus();
    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") loadStatus();
      timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    const onVisibility = () => { if (document.visibilityState === "visible") loadStatus(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadStatus]);

  // Toggle expand → lazy-load history on first open.
  const toggleExpand = useCallback(async (serviceKey) => {
    setExpanded((prev) => ({ ...prev, [serviceKey]: !prev[serviceKey] }));
    if (history[serviceKey] || historyLoading[serviceKey]) return;
    setHistoryLoading((prev) => ({ ...prev, [serviceKey]: true }));
    try {
      const body = await getServiceHealthHistory(serviceKey, 24);
      if (!isMountedRef.current) return;
      setHistory((prev) => ({ ...prev, [serviceKey]: body || { points: [], hours: 24 } }));
    } catch (err) {
      if (!isMountedRef.current) return;
      setHistory((prev) => ({ ...prev, [serviceKey]: { points: [], hours: 24, error: errMessage(err) } }));
    } finally {
      if (isMountedRef.current) setHistoryLoading((prev) => ({ ...prev, [serviceKey]: false }));
    }
  }, [history, historyLoading]);

  // Toggle is_ignored on/off. Optimistic update — the failing-count badge
  // flips immediately, no need to wait for the next poll.
  const handleToggleIgnore = useCallback(async (serviceKey, nextIgnored) => {
    setServices((prev) => prev.map((s) => s.service_key === serviceKey ? { ...s, is_ignored: nextIgnored } : s));
    try {
      await putServiceHealthIgnore(serviceKey, nextIgnored);
    } catch (err) {
      // revert on failure
      setServices((prev) => prev.map((s) => s.service_key === serviceKey ? { ...s, is_ignored: !nextIgnored } : s));
      setError(errMessage(err));
    }
  }, []);

  // Force-run one probe.
  const handleRunOne = useCallback(async (serviceKey) => {
    setRunning((prev) => ({ ...prev, [serviceKey]: true }));
    try {
      await postServiceHealthRunOne(serviceKey);
      await loadStatus();
      // history is stale — drop it so the next expand re-fetches.
      setHistory((prev) => { const next = { ...prev }; delete next[serviceKey]; return next; });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setRunning((prev) => ({ ...prev, [serviceKey]: false }));
    }
  }, [loadStatus]);

  // Force-run everything.
  const handleRunAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await postServiceHealthRunAll();
      await loadStatus();
      setHistory({});
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setRefreshing(false);
    }
  }, [loadStatus]);

  // Prefill: if the first load comes back with no probe rows (the scheduler
  // hasn't populated service_health yet), kick off one run automatically so the
  // panel isn't blank — but only once, and only for admins who can trigger it.
  const didAutoProbeRef = useRef(false);
  useEffect(() => {
    if (loading || refreshing || error || didAutoProbeRef.current) return;
    if (services.length === 0 && canManage) {
      didAutoProbeRef.current = true;
      handleRunAll();
    }
  }, [loading, refreshing, error, services, canManage, handleRunAll]);

  // Bucket services by group, preserving GROUP_ORDER. Ignored rows are
  // shown alongside the others (greyed) so the admin can still see + unmute,
  // but the per-group counters below filter them out of "failing".
  const grouped = useMemo(() => {
    const buckets = {};
    for (const k of GROUP_ORDER) buckets[k] = [];
    for (const s of services) {
      const list = buckets[s.service_group] || (buckets[s.service_group] = []);
      list.push(running[s.service_key] ? { ...s, status: "running" } : s);
    }
    return buckets;
  }, [services, running]);

  const failingInGroup = (rows) => rows.filter((s) => !s.is_ignored && s.status === "fail").length;
  const okInGroup      = (rows) => rows.filter((s) => !s.is_ignored && s.status === "ok").length;
  const skippedInGroup = (rows) => rows.filter((s) => !s.is_ignored && s.status === "skipped").length;

  // Overall summary. Ignored services count toward "muted" only — never
  // toward "failing" or "ok", per the whole point of the mute button.
  const summary = useMemo(() => {
    let ok = 0, fail = 0, skipped = 0, ignored = 0;
    for (const s of services) {
      if (s.is_ignored) { ignored++; continue; }
      if (s.status === "ok") ok++;
      else if (s.status === "fail") fail++;
      else if (s.status === "skipped") skipped++;
    }
    return { ok, fail, skipped, ignored, total: services.length };
  }, [services]);

  const overallState = summary.fail > 0 ? "fail" : summary.ok > 0 ? "ok" : "unknown";

  return (
    <div>
      {/* Panel action bar */}
      <div className="bl-glass" style={{
        padding: "1.1rem 1.5rem",
        marginBottom: "1.25rem",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1rem",
      }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h2 style={{
              margin: 0,
              fontFamily: "var(--bl-font-display)",
              fontSize: "1.1rem",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.1,
            }}>Services</h2>
            <StatusPill state={overallState} />
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--bl-text-soft)" }}>
            Live up/down for every external API the system depends on. The backend probes
            every 2 min and emails on state changes. This panel polls every 30 seconds while open.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
          <div style={{ textAlign: "right", fontSize: 12, color: "var(--bl-text-soft)" }}>
            <div>Last loaded</div>
            <div style={{ color: "var(--bl-text-strong, #e5e7f2)", fontWeight: 500 }}>
              {lastFetchedAt ? formatRelative(lastFetchedAt) : "never"}
            </div>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={handleRunAll}
              disabled={refreshing}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "0.6rem 1.1rem",
                borderRadius: 10,
                border: "none",
                background: "var(--bl-accent, #5758FC)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: refreshing ? "not-allowed" : "pointer",
                opacity: refreshing ? 0.6 : 1,
                boxShadow: "0 6px 20px rgba(87,88,252,0.35)",
              }}
            >
              <i className={`ri-${refreshing ? "loader-4-line" : "play-circle-line"}`} style={{ fontSize: 18 }} />
              {refreshing ? "Probing…" : "Probe all now"}
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        <SummaryCard label="All services"        total={summary.total} ok={summary.ok} failed={summary.fail} skipped={summary.skipped} icon="ri-shield-check-line" accent="var(--bl-accent)" />
        <SummaryCard label="Admin direct"        total={grouped.admin_direct?.length || 0}       ok={okInGroup(grouped.admin_direct || [])}       failed={failingInGroup(grouped.admin_direct || [])}       skipped={skippedInGroup(grouped.admin_direct || [])}       icon="ri-cloud-line"  accent="var(--bl-accent-light)" />
        <SummaryCard label="Biorlabs subsystems" total={grouped.biorlabs_federated?.length || 0} ok={okInGroup(grouped.biorlabs_federated || [])} failed={failingInGroup(grouped.biorlabs_federated || [])} skipped={skippedInGroup(grouped.biorlabs_federated || [])} icon="ri-stack-line"  accent="var(--bl-accent)" />
        <SummaryCard label="Biorlabs backend"    total={grouped.biorlabs_self?.length || 0}      ok={okInGroup(grouped.biorlabs_self || [])}      failed={failingInGroup(grouped.biorlabs_self || [])}      skipped={skippedInGroup(grouped.biorlabs_self || [])}      icon="ri-server-line" accent="var(--bl-success)" />
      </div>

      {error && (
        <div className="bl-glass" style={{
          padding: "0.85rem 1.25rem",
          marginBottom: "1.25rem",
          background: "rgba(255,93,108,0.08)",
          border: "1px solid rgba(255,93,108,0.25)",
          color: "#ffb1ba",
          fontSize: 13,
        }}>
          <i className="ri-error-warning-line" style={{ marginRight: 8 }} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="bl-glass" style={{ padding: "2rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
          Loading service health…
        </div>
      ) : services.length === 0 ? (
        <div className="bl-glass" style={{ padding: "2rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
          No probes registered yet. The scheduler runs every 2 minutes — give it a few minutes after deploy.
        </div>
      ) : (
        GROUP_ORDER.map((groupKey) => {
          const rows = grouped[groupKey] || [];
          if (rows.length === 0) return null;
          const meta = GROUP_META[groupKey];
          const groupOk     = okInGroup(rows);
          const groupFailed = failingInGroup(rows);
          return (
            <div key={groupKey} className="bl-glass" style={{ padding: 0, marginBottom: "1.25rem", overflow: "hidden" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.85rem",
                padding: "1rem 1.25rem",
                borderBottom: "1px solid var(--bl-line)",
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: "rgba(87,88,252,0.16)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <i className={meta.icon} style={{ color: "var(--bl-accent)", fontSize: 17 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    margin: 0,
                    fontFamily: "var(--bl-font-display)",
                    fontSize: "1.02rem",
                    color: "#fff",
                    fontWeight: 600,
                  }}>{meta.label}</h3>
                  <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "var(--bl-text-soft)" }}>{meta.description}</p>
                </div>
                <div style={{ fontSize: 12, color: "var(--bl-text-soft)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  <span style={{ color: "#7be09a" }}>{groupOk}</span> ok
                  {groupFailed > 0 && <>{" · "}<span style={{ color: "#ff8a98" }}>{groupFailed}</span> failing</>}
                  {" · "}{rows.length} total
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
                  <TableHeader />
                  <tbody>
                    {rows.map((s) => (
                      <ServiceRow
                        key={s.service_key}
                        service={s}
                        onForceRun={handleRunOne}
                        onToggleIgnore={handleToggleIgnore}
                        canManage={canManage}
                        expanded={!!expanded[s.service_key]}
                        onToggle={() => toggleExpand(s.service_key)}
                        history={history[s.service_key]}
                        historyLoading={!!historyLoading[s.service_key]}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ServicesPanel;
